/**
 * Shared visual chrome for Admin PWA install / update / offline banners.
 * Used for both surfaces: tenant (`/`) and Dev (`/dev*`) — one shell, two copy keys.
 *
 * Must stay above sticky table cols / toolbar / bottom nav (`--z-bn` = 120)
 * and below toasts (`--z-toast` = 400) so top-right toasts never paint under
 * the TEST economy strip (same body portal stack).
 */

export const ADMIN_PWA_LIFECYCLE_CHROME_Z_INDEX = 'var(--z-banner)';

/** Inline styles — do not rely only on Tailwind arbitrary z-[] (must win over sticky lists). */
export function adminPwaLifecycleChromeStyle(
  bottomChromeCss = 'var(--admin-bottom-chrome, 0px)',
  keyboardInsetCss = 'var(--keyboard-inset, 0px)',
): { readonly bottom: string; readonly zIndex: string } {
  return {
    bottom: `calc(${bottomChromeCss} + ${keyboardInsetCss})`,
    zIndex: ADMIN_PWA_LIFECYCLE_CHROME_Z_INDEX,
  };
}

/**
 * Shell classes shared by tenant home and /dev install / update offers.
 * Mirrors customer CookieConsentBanner: bottom band, max-w-xl, px-3, pb/pt rhythm.
 * Safe-area lives in --admin-bottom-chrome (nav), so band padding matches cookie pb-2.
 * Keep admin-safe-area-x + px-3 — cookie stacks customer-safe-area-x + px-3 the same way.
 */
export const ADMIN_PWA_LIFECYCLE_CHROME_CLASSNAME =
  'admin-safe-area-x pointer-events-none fixed inset-x-0 mx-auto flex max-w-xl flex-col gap-2 px-3 pb-2 pt-3 isolate';

/**
 * Extra Card chrome — cookie banner uses border + popover elevation.
 * Deliberate admin substitute for customer --shadow-popover: OfflineBanner family --elevation-2.
 */
export const ADMIN_PWA_LIFECYCLE_CARD_CLASSNAME =
  'pointer-events-auto border border-[var(--color-an-border)] shadow-[var(--elevation-2)]';

/** CookieConsentBanner action row — stack on narrow, equal flex columns from 30rem. */
export const ADMIN_PWA_LIFECYCLE_ACTIONS_CLASSNAME =
  'mt-3 flex flex-col gap-2 min-[30rem]:flex-row min-[30rem]:items-stretch';

export const ADMIN_PWA_LIFECYCLE_ACTION_BUTTON_CLASSNAME =
  'w-full whitespace-nowrap min-[30rem]:min-w-0 min-[30rem]:flex-1';
